// @ts-ignore
import ICAL from "ical.js";
import moment from "moment";
import { Country, Holiday, HolidaysCalendar } from "shared/types/calendar";
import { GeneratedSchedugramServerClient } from "./generated-schedugram-server-client";
import type {
  components,
  paths,
} from "./generated/schedugram-server-openapi-types";

const generatedSchedugramServerClient =
  new GeneratedSchedugramServerClient<paths>();

type ImportCountryHolidaysRequestBody =
  components["schemas"]["ImportCountryHolidaysRequestBody"];
type ImportCalendarRequestBody =
  components["schemas"]["ImportCalendarRequestBody"];
type CalendarCountryResponse = components["schemas"]["CalendarCountryResponse"];
type CalendarResponse = components["schemas"]["CalendarResponse"];

export const fetchCountriesList = async (): Promise<Country[]> => {
  const result = await generatedSchedugramServerClient.client.GET(
    "/calendar/countries"
  );

  return (await generatedSchedugramServerClient.unwrap(result)).map(toCountry);
};

export const fetchCountryHolidays = async ({
  name,
  iso,
}: Country): Promise<HolidaysCalendar> => {
  const body: ImportCountryHolidaysRequestBody = {
    countryName: name,
    iso,
  };
  const result = await generatedSchedugramServerClient.client.POST(
    "/calendars/import-country-holidays",
    { body }
  );

  return toHolidaysCalendar(
    await generatedSchedugramServerClient.unwrap(result)
  );
};

export const fetchHolidayCalendars = async (): Promise<HolidaysCalendar[]> => {
  const result = await generatedSchedugramServerClient.client.GET("/calendars");
  return (await generatedSchedugramServerClient.unwrap(result)).map(
    toHolidaysCalendar
  );
};

export const removeCountryHolidays = async (
  calendarId: string
): Promise<void> => {
  const result = await generatedSchedugramServerClient.client.DELETE(
    "/calendars/{calendarId}",
    {
      params: {
        path: { calendarId },
      },
    }
  );

  await generatedSchedugramServerClient.ensureOk(result);
};

export const importHolidaysFromUrl = async ({
  calendarName,
  url,
}: {
  calendarName: string;
  url: string;
}): Promise<HolidaysCalendar> => {
  const body: ImportCalendarRequestBody = {
    calendarName,
    url,
  };
  const result = await generatedSchedugramServerClient.client.POST(
    "/calendars/import",
    { body }
  );

  return toHolidaysCalendar(
    await generatedSchedugramServerClient.unwrap(result)
  );
};

export const fetchHolidaysFromApi = async (
  countryIso: string
): Promise<Holiday[]> => {
  const result = await generatedSchedugramServerClient.client.GET(
    "/calendar/holidays",
    {
      params: { query: { countryIso } },
    }
  );
  const data = await generatedSchedugramServerClient.unwrap(result);

  return parseIcsEvents(data.ics, true);
};

export function fetchFromUrl(
  url: string,
  withName: true
): Promise<{ name?: string; events: Holiday[] }>;
export function fetchFromUrl(url: string, withName?: false): Promise<Holiday[]>;
export async function fetchFromUrl(
  url: string,
  withName?: boolean
): Promise<Holiday[] | { name?: string; events: Holiday[] }> {
  const result = await generatedSchedugramServerClient.client.GET(
    "/calendar/import-url",
    {
      params: { query: { url } },
    }
  );
  const data = await generatedSchedugramServerClient.unwrap(result);

  return parseIcsEvents(data, false, withName);
}

function parseCalendarName(headers: [string[]]) {
  const header = headers.find((item: string[]) => item[0] === "x-wr-calname");
  return header?.[header?.length - 1];
}

function parseIcsEvents(ics: string, normalise?: boolean, withName?: boolean) {
  let parsedData;
  try {
    parsedData = ICAL.parse(ics);
  } catch (err) {
    throw new Error("Failed to parse ICS", { cause: err });
  }

  const events = parsedData[2].reduce((events: any, event: any) => {
    if (event[0] !== "vevent") return events;

    const newEvent = event[1].reduce(function (newEvent: any, item: any) {
      const value = item[3];

      switch (item[0]) {
        case "location":
          newEvent.location = value;
          break;
        case "summary":
          newEvent.title = normalise ? normaliseSummary(value) : value;
          break;
        case "dtstart":
          newEvent.start = normalise ? moment.utc(value) : moment(value);
          break;
        case "rrule":
          newEvent.rrule = value;
          break;
        case "recurrence-id":
          newEvent.recurrenceId = value;
          break;
      }
      return newEvent;
    }, {});

    if (newEvent.title && newEvent.start) {
      if (newEvent.recurrenceId && !newEvent.rrule) return events;

      newEvent.rendering = "background";
      events.push(newEvent);
    }
    return events;
  }, []);

  if (withName) {
    const calendarName = parseCalendarName(parsedData[1]);
    return { name: calendarName, events };
  }

  return events;
}

function normaliseSummary(text: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "text/html");
  return doc.documentElement.textContent;
}

function toCountry(country: CalendarCountryResponse): Country {
  if (!country._id) {
    throw new Error("Calendar country response is missing _id");
  }

  return {
    _id: country._id,
    iso: country.iso,
    name: country.name,
  };
}

function toHolidaysCalendar(calendar: CalendarResponse): HolidaysCalendar {
  if (!calendar._id) {
    throw new Error("Calendar response is missing _id");
  }

  return {
    _id: calendar._id,
    type: calendar.type,
    countryIso: calendar.countryIso ?? "",
    name: calendar.name,
    url: calendar.url ?? "",
  };
}
